import { ImageName } from "@mcleod/images";
import { Button, Event, DataSourceMode, Image, Label, Layout, Panel, SlideoutDecorator, SlideoutDecoratorProps, Step, TableRow, DataSourceModeChangeEvent } from "@mcleod/components";
import { HorizontalAlignment, ModelRow, Navigation, StringUtil } from "@mcleod/core";
import { OrderTabView } from "../OrderTabView";
import { AutogenLayoutPreassignment } from "./autogen/AutogenLayoutPreassignment";
import { RowDriverOnlyFields } from "../../../dispatch/asset/src/models/autogen/AutogenModelDriverOnly";

export interface PreassignmentSlideoutProps {
    movementId: string;
    orderId: string;
    preassigned: boolean;
    manifestId?: string,
    unassigning?: boolean;
    equipmentGroupId?: string;
    tractor?: string;
    driver1Data?: Partial<RowDriverOnlyFields>;
    driver2Data?: Partial<RowDriverOnlyFields>;
    trailer?: string;
    trailer2?: string;
    trailer3?: string;
    loadMatchLock?: boolean,
    doAfterSlideout?: (name: string, showToast: boolean, multipleSelected?: boolean) => void;
    sourceEvent?: Event;
    feasibilityAssignReason?: ModelRow<any>
    selectedRows?: TableRow[];
    linkedId?: string;
}

const actionButtonProps = {
    caption: "Preassign & Continue",
    backgroundColor: "primary",
    color: "primary.reverse",
    busyWhenDataSourceBusy: true,
    paddingTop: 10,
    paddingBottom: 10,
    rowBreak: false,
    borderWidth: 0
}

export class Preassignment extends AutogenLayoutPreassignment {

    public static showSlideout(props: PreassignmentSlideoutProps, slideoutProps?: Partial<SlideoutDecoratorProps>) {
        let busyButton: Button;
        if (props.sourceEvent?.target instanceof Button) {
            busyButton = props.sourceEvent.target;
            busyButton.busy = true;
        }
        const self = Layout.getLayout("lme/dispatch/asset/Preassignment") as Preassignment;
        const actionButton = new Button({...actionButtonProps});
        const continueButton = new Button({...actionButtonProps, caption: "Continue", visible: false});
        const { orderId, manifestId, preassigned, equipmentGroupId, movementId, tractor,
            driver1Data, driver2Data, trailer, trailer2, trailer3, unassigning, loadMatchLock, feasibilityAssignReason, selectedRows, linkedId } = props;
        const isMultiSelect = selectedRows != null || linkedId != null;
        const slideoutTitle = isMultiSelect ? "Assigning Equipment to Multiple Orders" :
            !StringUtil.isEmptyString(props.manifestId) ? `Assignments - ${props.manifestId}` :
             !StringUtil.isEmptyString(props.orderId)? `Assignments - ${props.orderId}`: "Assignments -";
        const slideout = new SlideoutDecorator({
            layout: self,
            width: '80%',
            fillVerticalSpace: true,
            title: slideoutTitle,
            overlayProps: { greyedBackground: true },
            addlComponents: [continueButton,actionButton],
            doAfterSlideIn: async () => {
                const stepperLayout = self.layoutPreassignmentStepper;
                if (busyButton != null) {
                    busyButton.busy = false;
                }
                if (isMultiSelect) {
                    stepperLayout.isMultiSelect = true;
                } else {
                    stepperLayout.orderId = orderId;
                    stepperLayout.manifestId = manifestId;
                    stepperLayout.movementId = movementId;
                    stepperLayout.preassigned = preassigned;
                    stepperLayout.unassigning = unassigning ?? false;
                    stepperLayout.sourceMovementStops.addAfterExecutionListener(() => setupSlideoutHeader());
                }
                stepperLayout.sourceMovementStops.orderBy = "stop.movement_sequence asc";
                stepperLayout.sourceMovementStops.search({ id: movementId });
                stepperLayout.mainDataSource.mode = DataSourceMode.ADD;
                stepperLayout.setupActionButton(actionButton);
                stepperLayout.setupContinueButton(continueButton);
                stepperLayout.stepperPreassignment.enforceForwardValidation = !preassigned;

                stepperLayout.mainDataSource.addAfterModeChangeListener(async (event: DataSourceModeChangeEvent) => {
                    if (event.newMode == DataSourceMode.ADD) {
                        if (tractor) {
                            stepperLayout.activeRow?.setValueAndLookupModelData("tractor_id", tractor,
                                new ModelRow(stepperLayout.textboxTractor.lookupModel, false, {"id": tractor}));
                            if (loadMatchLock) {
                                stepperLayout.textboxTractor.enabled = false;
                            }
                        }
                        if (driver1Data) {
                            stepperLayout.activeRow?.setValueAndLookupModelData("driver_id", driver1Data.id, driver1Data);
                            if (loadMatchLock) {
                                stepperLayout.textboxDriverOne.enabled = false;
                            }
                        }
                        if (driver2Data) {
                            stepperLayout.activeRow?.setValueAndLookupModelData("driver_two_id", driver2Data.id, driver2Data);
                        }
                        if (trailer) {
                            stepperLayout.activeRow?.setValueAndLookupModelData("trailer_id", trailer,
                                new ModelRow(stepperLayout.textboxTrailer.lookupModel, false, {"id": trailer}));
                        }
                        if (trailer2) {
                            stepperLayout.activeRow?.setValueAndLookupModelData("trailer2_id", trailer2,
                                new ModelRow(stepperLayout.textboxTrailer2.lookupModel, false, {"id": trailer2}));
                        }
                        if (trailer3) {
                            stepperLayout.activeRow?.setValueAndLookupModelData("trailer3_id", trailer3,
                                new ModelRow(stepperLayout.textboxTrailer3.lookupModel, false, {"id": trailer3}));
                        }
                        if (feasibilityAssignReason) {
                            stepperLayout.activeRow?.set("feasibility_assign_reason", feasibilityAssignReason)
                        }
                        stepperLayout.setupZmit();
                        if (isMultiSelect) {
                            stepperLayout.panelEquipmentGroupWarnings.visible = stepperLayout.textboxTrailer.enabled =
                            stepperLayout.textboxTrailer2.enabled = stepperLayout.textboxTrailer3.enabled = false;
                            const multiPreassignment = stepperLayout.layoutMultiPreassignment;
                            const validationProps = { preassignment: true };
                            multiPreassignment.visible = true;
                            multiPreassignment.tractorId = validationProps["tractor_id"] = tractor ?? null;
                            multiPreassignment.driverId = validationProps["driver_id"] = driver1Data?.id ?? null;
                            multiPreassignment.driver2Id = validationProps["driver2_id"] = driver2Data?.id ?? null;
                            multiPreassignment.linkedId = linkedId ?? null;
                            await multiPreassignment.mainDataSource.search({
                                ...(linkedId != null ?
                                    { linked_id: linkedId } :
                                    { movement_ids: selectedRows.map(r => r.data.get("movement_id")) }),
                                ...validationProps
                            });
                        } else if (!preassigned && (tractor || (driver1Data?.id || driver2Data?.id) || trailer || trailer2 || trailer3)) {
                            stepperLayout.validateEquipmentGroup({
                                preassignment: true,
                                modelRow: stepperLayout.activeRow,
                                equipmentGroupWarnings: stepperLayout.layoutEquipmentGroupWarnings,
                                movementId, orderId,
                                tractorId: tractor, driverId: driver1Data?.id, driver2Id: driver2Data?.id,
                                trailerId: trailer, trailer2Id: trailer2, trailer3Id: trailer3
                            });
                        }
                    }
                });
                stepperLayout.layoutEquipmentAssignments.addLayoutDataLoadListener(async ()=>{
                    const row = stepperLayout.sourcePreassignments.activeRow;
                    await self.layoutDispatchEquipAlerts.search({units: [row.get("tractor_id"),row.get("driver_id"),row.get("trailer_id")]});
                    const rows = self.layoutDispatchEquipAlerts.mainDataSource.data;
                    const urgentCount = rows.reduce((acc, cur) => cur.get("priority") == "U" ? ++acc : acc, 0);
                    const tab = self.tabEquipmentAlerts;
                    if(rows.length > 0){
                        tab.imageName = ImageName.equipmentAlerts;
                    }
                    if(urgentCount > 0 ){
                        tab.imageName = ImageName.equipmentAlertUrgent;
                    }
                    tab.visible = true;
                });
                if (preassigned && !isMultiSelect) {
                    stepperLayout.stepperPreassignment.components.forEach((comp: Step, index: number) => {
                        if (index < 1) comp.visited = true
                    });
                    stepperLayout.stepperPreassignment.selectedIndex = stepperLayout.stepAssignments.index;

                    if (equipmentGroupId) {
                        await stepperLayout.searchEquipmentGroup(equipmentGroupId);
                        if (preassigned) {
                            stepperLayout.layoutEquipmentAssignments.textboxEquipment.visible = true;
                            stepperLayout.layoutEquipmentAssignments.setup(movementId, stepperLayout.getEquipmentId(), tractor);
                        }
                    }
                }
            },
            doAfterSlideOut: (decorator:SlideoutDecorator) => {
                const stepperLayout = self.layoutPreassignmentStepper;
                if (stepperLayout.stepperPreassignment.selectedIndex != 0)
                    props.doAfterSlideout?.(stepperLayout.textboxDriverOne.text, stepperLayout.showAssignToast, isMultiSelect);
                slideoutProps?.doAfterSlideOut?.(decorator);
            },
        });

        const setupSlideoutHeader = () => {
            const stepperLayout = self.layoutPreassignmentStepper;
            const stopsData = stepperLayout.sourceMovementStops.data;
            const originStop = stopsData[0]
            const destStop = stopsData[stopsData.length-1];
            const originStopType = originStop.get("stop.stop_type");
            const destStopType = destStop.get("stop.stop_type");

            if (originStopType == "SP" || destStopType == "SD") {
                const imageName = originStopType === "SP" && destStopType === "SD" ? ImageName.ysplitComplete
                    : originStopType === "SP" ? ImageName.ysplitPick : destStopType === "SD" ? ImageName.ysplit : null;
                const imageYsplit = new Image({
                    name: imageName, rowBreak: false, marginLeft: 8,
                    color: "default.reverse"
                });
                slideout.panelHeader.insert(imageYsplit, 1);
            }

            const stopLabel = new Label({
                text: `  ${originStop.get("stop.city_name")}, ${originStop.get("stop.state")} to ` +
                `${destStop.get("stop.city_name")}, ${destStop.get("stop.state")}`,
                fontBold: true, fontSize: "large",
                rowBreak: false
            });
            slideout.panelHeader.insert(stopLabel, 1);
        }
    }

    static getResultsToastPanel(orderId: string, driverName: string, manifestId?: string, unassign?: boolean): Panel {
        const labelProps = { color: "white", fontSize: "xlarge", rowBreak: false, paddingLeft: 3, paddingRight: 1, marginLeft: 0, marginRight: 0, wrap: false };
        const labelLinkProps = { ...labelProps, style: { textDecoration: "underline" } };
        const isManifest = !StringUtil.isEmptyString(manifestId);
        const labelText = isManifest ? "Manifest " : "Order ";
        const labelId = isManifest ? manifestId : orderId;
        const toastMessagePrefix = new Label({ ...labelProps, text: labelText });
        const assignmentText = unassign ? "unassigned from " : "assigned to ";
        const toastMessagePostfix = new Label({ ...labelProps, text: "  was successfully " + assignmentText + driverName });
        const toastLink = new Label({ ...labelLinkProps, text: labelId,
            onClick: event => {
                if (isManifest) {
                    Navigation.navigateTo("lme/dispatch/OrderList?manifest_id=" + manifestId, { newTab: true });
                } else {
                    OrderTabView.navigateTo({ key: labelId, newTab: true });
                }
            }
        });
        return new Panel({
            align: HorizontalAlignment.CENTER, fillRow: true, width: window.innerWidth,
            components: [ toastMessagePrefix, toastLink, toastMessagePostfix ]
        });
    }

    /** This is an event handler for the afterTabSelection event of tabsetPreassignment. */
    tabsetPreassignmentAfterTabSelection(event) {
        if(this.layoutPreassignmentStepper.actionButton != null)
            this.layoutPreassignmentStepper.actionButton.visible = event.newValue != 1;
        if(this.layoutPreassignmentStepper.continueButton != null)
            this.layoutPreassignmentStepper.continueButton.visible = event.newValue != 1;
    }
}
